import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Zap,
  Shield,
  Sparkles,
  Eye,
  Code,
  FileText,
  Mail,
  FileType,
  UserPlus,
  Grid3X3,
  Download,
  Users,
  Heart,
  Send,
  MessageCircle,
  Brain,
  FileSearch,
  Wand2,
  Bot,
  Star,
  ChevronRight,
  Clock,
  Palette,
  Globe
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import GlowCard from '@/components/GlowCard';
import { useAuth } from '@/hooks/useAuth';
import tamLogo from '@/assets/tam-logo.png';

const features = [
  {
    icon: Heart,
    title: '100% Free',
    description: 'No hidden costs, no credit limits. Free forever for everyone.',
    gradient: 'from-pink-500 to-rose-500',
  },
  {
    icon: Shield,
    title: 'Privacy First',
    description: 'No data stored on servers. Process locally when possible.',
    gradient: 'from-green-500 to-emerald-500',
  },
  {
    icon: Zap,
    title: 'AI Powered',
    description: 'Cutting-edge AI tools for detection, paraphrasing & more.',
    gradient: 'from-yellow-500 to-orange-500',
  },
  {
    icon: Globe,
    title: 'Cloud Access',
    description: 'Access your tools from anywhere, on any device.',
    gradient: 'from-blue-500 to-cyan-500',
  },
];

const toolCategories = [
  {
    icon: Brain,
    title: 'AI Tools',
    tools: ['AI Detector', 'Paraphraser', 'Plagiarism Checker', 'Prompt Generator'],
    path: '/dashboard/tools',
    gradient: 'from-purple-500 to-pink-500',
    badge: 'Popular',
  },
  {
    icon: FileSearch,
    title: 'PDF Tools',
    tools: ['PDF Analyzer', 'PDF Merge', 'PDF Split', 'PDF to Image'],
    path: '/dashboard/tools',
    gradient: 'from-blue-500 to-cyan-500',
    badge: 'New',
  },
  {
    icon: Code,
    title: 'Developer Tools',
    tools: ['Code Studio', 'JSON Formatter', 'Hash Generator', 'UUID Generator'],
    path: '/dashboard/tools',
    gradient: 'from-green-500 to-emerald-500',
    badge: null,
  },
  {
    icon: FileText,
    title: 'Document Tools',
    tools: ['Resume Builder', 'Word Counter', 'Lorem Ipsum', 'Markdown to HTML'],
    path: '/dashboard/tools',
    gradient: 'from-orange-500 to-red-500',
    badge: null,
  },
];

const steps = [
  {
    icon: UserPlus,
    title: 'Sign Up Free',
    description: 'Create account in seconds',
    gradient: 'from-blue-500 to-purple-500',
  },
  {
    icon: Grid3X3,
    title: 'Explore 36+ Tools',
    description: 'Find the perfect tool for you',
    gradient: 'from-purple-500 to-pink-500',
  },
  {
    icon: Download,
    title: 'Get Results',
    description: 'Export, download, share instantly',
    gradient: 'from-pink-500 to-red-500',
  },
];

const popularTools = [
  {
    title: 'AI Detector',
    description: 'Detect AI-generated content with 95%+ accuracy. Perfect for teachers & writers.',
    icon: Bot,
    path: '/dashboard/tools/ai-detector',
    gradient: 'from-violet-500 to-purple-500',
    tag: 'AI Powered',
  },
  {
    title: 'PDF Analyzer',
    description: 'Extract text, analyze & chat with your PDFs using AI. Upload and ask questions.',
    icon: FileSearch,
    path: '/dashboard/tools/pdf-analyzer',
    gradient: 'from-blue-500 to-cyan-500',
    tag: 'New',
  },
  {
    title: 'Paraphraser',
    description: 'Rewrite text in multiple tones. Make content unique & plagiarism-free.',
    icon: Wand2,
    path: '/dashboard/tools/paraphraser',
    gradient: 'from-pink-500 to-rose-500',
    tag: 'AI Powered',
  },
  {
    title: 'Code Studio',
    description: 'Full-featured online IDE. Write HTML, CSS, JS with live preview.',
    icon: Code,
    path: '/dashboard/tools/code-studio',
    gradient: 'from-green-500 to-emerald-500',
    tag: 'Popular',
  },
  {
    title: 'Resume Builder',
    description: 'Create stunning resumes with professional templates. Export as PDF.',
    icon: FileText,
    path: '/dashboard/resume-builder',
    gradient: 'from-orange-500 to-amber-500',
    tag: 'Featured',
  },
  {
    title: 'Temp Mail',
    description: 'Generate disposable email addresses. Receive emails in real-time.',
    icon: Mail,
    path: '/dashboard/temp-mail',
    gradient: 'from-cyan-500 to-blue-500',
    tag: 'Popular',
  },
];

const stats = [
  { value: '36+', label: 'Free Tools', icon: Sparkles },
  { value: '10K+', label: 'Happy Users', icon: Users },
  { value: '100%', label: 'Free Forever', icon: Heart },
  { value: '5', label: 'AI Tools', icon: Brain },
];

const Index = () => {
  const { user } = useAuth();

  return (
    <div className="min-h-screen pt-16 overflow-hidden">
      {/* Animated Background */}
      <div className="fixed inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-br from-background via-background to-primary/5" />
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-conic from-primary/5 via-transparent to-purple-500/5 rounded-full blur-3xl opacity-50" />
      </div>

      {/* Hero Section */}
      <section className="relative py-16 lg:py-28 overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="flex flex-col items-center text-center">
            {/* Animated Logo */}
            <div className="mb-8 relative group">
              <div className="absolute inset-0 bg-gradient-to-r from-primary via-purple-500 to-pink-500 rounded-full blur-2xl opacity-50 group-hover:opacity-75 transition-opacity animate-pulse" />
              <img
                src={tamLogo}
                alt="TAM Store"
                className="relative h-28 w-28 md:h-36 md:w-36 rounded-full shadow-2xl border-4 border-primary/30 animate-float"
                style={{
                  boxShadow: '0 0 80px rgba(59, 130, 246, 0.4), 0 0 160px rgba(139, 92, 246, 0.2)',
                }}
              />
              {/* Orbiting particles */}
              <div className="absolute inset-0 animate-spin-slow" style={{ animationDuration: '20s' }}>
                <div className="absolute -top-2 left-1/2 w-3 h-3 bg-primary rounded-full shadow-lg shadow-primary/50" />
              </div>
              <div className="absolute inset-0 animate-spin-slow" style={{ animationDuration: '15s', animationDirection: 'reverse' }}>
                <div className="absolute -bottom-2 right-0 w-2 h-2 bg-purple-500 rounded-full shadow-lg shadow-purple-500/50" />
              </div>
            </div>

            {/* Badge */}
            <div className="mb-6 animate-slide-up">
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-primary/20 to-purple-500/20 border border-primary/30 text-sm font-medium">
                <Sparkles className="h-4 w-4 text-primary animate-pulse" />
                <span className="bg-gradient-to-r from-primary to-purple-400 bg-clip-text text-transparent">
                  Free Professional Tools for Everyone
                </span>
              </span>
            </div>

            {/* Main heading with animated gradient */}
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6 max-w-4xl animate-slide-up" style={{ animationDelay: '0.1s' }}>
              Your Ultimate{' '}
              <span className="relative inline-block">
                <span className="bg-gradient-to-r from-primary via-purple-500 to-pink-500 bg-clip-text text-transparent animate-gradient bg-[length:200%_auto]">
                  Digital Toolkit
                </span>
                <Sparkles className="absolute -top-2 -right-8 h-6 w-6 text-yellow-400 animate-pulse" />
              </span>
            </h1>

            <p className="text-lg md:text-xl text-muted-foreground mb-8 max-w-2xl animate-slide-up" style={{ animationDelay: '0.2s' }}>
              36+ powerful AI & productivity tools. PDF Analyzer, AI Detector, Paraphraser,
              Code Studio, Resume Builder & more. <span className="text-primary font-medium">100% Free.</span>
            </p>

            {/* Animated Stats */}
            <div className="flex flex-wrap justify-center gap-4 md:gap-8 mb-10 animate-slide-up" style={{ animationDelay: '0.3s' }}>
              {stats.map((stat, index) => (
                <div
                  key={stat.label}
                  className="relative group"
                >
                  <div className="flex items-center gap-3 px-5 py-3 rounded-2xl bg-card/50 border border-border/50 backdrop-blur-sm hover:border-primary/50 transition-all hover:scale-105">
                    <div className="p-2 rounded-lg bg-primary/10">
                      <stat.icon className="h-5 w-5 text-primary" />
                    </div>
                    <div className="text-left">
                      <div className="text-2xl font-bold bg-gradient-to-r from-white to-white/80 bg-clip-text text-transparent">{stat.value}</div>
                      <div className="text-xs text-muted-foreground">{stat.label}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 animate-slide-up" style={{ animationDelay: '0.4s' }}>
              {user ? (
                <Link to="/dashboard">
                  <Button size="lg" className="gap-2 px-8 h-14 text-lg bg-gradient-to-r from-primary to-purple-600 hover:from-primary/90 hover:to-purple-600/90 shadow-lg shadow-primary/25 hover:shadow-primary/40 transition-all hover:scale-105">
                    Go to Dashboard
                    <ArrowRight className="h-5 w-5" />
                  </Button>
                </Link>
              ) : (
                <>
                  <Link to="/signup">
                    <Button size="lg" className="gap-2 px-8 h-14 text-lg bg-gradient-to-r from-primary to-purple-600 hover:from-primary/90 hover:to-purple-600/90 shadow-lg shadow-primary/25 hover:shadow-primary/40 transition-all hover:scale-105">
                      Get Started Free
                      <ArrowRight className="h-5 w-5" />
                    </Button>
                  </Link>
                  <Link to="/login">
                    <Button size="lg" variant="outline" className="px-8 h-14 text-lg border-primary/50 hover:bg-primary/10 hover:border-primary transition-all">
                      Sign In
                    </Button>
                  </Link>
                </>
              )}
            </div>

            {/* Trust badge */}
            <div className="mt-8 flex items-center gap-2 text-sm text-muted-foreground animate-fade-in" style={{ animationDelay: '0.5s' }}>
              <div className="flex -space-x-2">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-purple-500 border-2 border-background flex items-center justify-center text-xs font-bold text-white">
                    {['T', 'A', 'M', '💜'][i]}
                  </div>
                ))}
              </div>
              <span>Trusted by <span className="text-foreground font-medium">10,000+</span> professionals</span>
            </div>
          </div>
        </div>
      </section>

      {/* Popular Tools Section - NEW */}
      <section className="py-16 lg:py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4">
              <Star className="h-4 w-4" /> Most Popular
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
              Featured <span className="bg-gradient-to-r from-primary to-purple-500 bg-clip-text text-transparent">Tools</span>
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Discover our most loved tools, powered by AI and built for productivity.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {popularTools.map((tool, index) => (
              <div
                key={tool.title}
                className="animate-slide-up group"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <Link to={user ? tool.path : '/login'}>
                  <div className="relative h-full p-6 rounded-2xl bg-card/50 border border-border/50 backdrop-blur-sm hover:border-primary/50 transition-all duration-300 hover:shadow-xl hover:shadow-primary/10 hover:-translate-y-1 overflow-hidden">
                    {/* Gradient background on hover */}
                    <div className={`absolute inset-0 bg-gradient-to-br ${tool.gradient} opacity-0 group-hover:opacity-5 transition-opacity`} />

                    {/* Tag */}
                    <span className={`absolute top-4 right-4 px-2 py-1 rounded-full text-xs font-medium bg-gradient-to-r ${tool.gradient} text-white`}>
                      {tool.tag}
                    </span>

                    <div className="relative">
                      {/* Icon */}
                      <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${tool.gradient} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform shadow-lg`}>
                        <tool.icon className="h-7 w-7 text-white" />
                      </div>

                      <h3 className="text-xl font-semibold mb-2 group-hover:text-primary transition-colors">{tool.title}</h3>
                      <p className="text-muted-foreground text-sm mb-4">{tool.description}</p>

                      <div className="flex items-center text-primary text-sm font-medium">
                        Try Now <ChevronRight className="h-4 w-4 ml-1 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </div>
                </Link>
              </div>
            ))}
          </div>

          <div className="text-center mt-10">
            <Link to={user ? "/dashboard/tools" : "/login"}>
              <Button size="lg" variant="outline" className="gap-2 border-primary/50 hover:bg-primary/10">
                View All 36+ Tools
                <ArrowRight className="h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Tool Categories Section */}
      <section className="py-16 lg:py-20 bg-gradient-to-b from-transparent via-secondary/30 to-transparent">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Explore by <span className="bg-gradient-to-r from-primary to-purple-500 bg-clip-text text-transparent">Category</span>
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Everything you need, organized and easy to find.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {toolCategories.map((category, index) => (
              <div
                key={category.title}
                className="animate-slide-up"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <Link to={user ? category.path : '/login'}>
                  <div className="relative h-full p-6 rounded-2xl bg-card/50 border border-border/50 backdrop-blur-sm hover:border-primary/50 transition-all duration-300 hover:shadow-lg hover:-translate-y-1 group overflow-hidden">
                    {/* Badge */}
                    {category.badge && (
                      <span className={`absolute top-4 right-4 px-2 py-0.5 rounded-full text-xs font-medium bg-gradient-to-r ${category.gradient} text-white`}>
                        {category.badge}
                      </span>
                    )}

                    <div className="flex items-center gap-3 mb-4">
                      <div className={`p-3 rounded-xl bg-gradient-to-br ${category.gradient} group-hover:scale-110 transition-transform shadow-lg`}>
                        <category.icon className="h-6 w-6 text-white" />
                      </div>
                      <h3 className="text-lg font-semibold">{category.title}</h3>
                    </div>

                    <ul className="space-y-2 mb-4">
                      {category.tools.map((tool) => (
                        <li key={tool} className="text-sm text-muted-foreground flex items-center gap-2">
                          <div className={`w-1.5 h-1.5 rounded-full bg-gradient-to-r ${category.gradient}`} />
                          {tool}
                        </li>
                      ))}
                    </ul>

                    <div className="flex items-center text-primary text-sm font-medium">
                      Explore <ChevronRight className="h-4 w-4 ml-1 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-16 lg:py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Get Started in <span className="bg-gradient-to-r from-primary to-purple-500 bg-clip-text text-transparent">30 Seconds</span>
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Simple, fast, and straightforward. No learning curve required.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {steps.map((step, index) => (
              <div
                key={step.title}
                className="animate-slide-up text-center relative"
                style={{ animationDelay: `${index * 0.15}s` }}
              >
                <div className="relative mb-6">
                  <div className={`w-20 h-20 mx-auto rounded-2xl bg-gradient-to-br ${step.gradient} flex items-center justify-center shadow-lg`}>
                    <step.icon className="h-10 w-10 text-white" />
                  </div>
                  <div className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-card border-2 border-primary text-primary flex items-center justify-center font-bold text-sm">
                    {index + 1}
                  </div>
                  {/* Connector line */}
                  {index < steps.length - 1 && (
                    <div className="hidden md:block absolute top-10 left-[60%] w-full h-0.5 bg-gradient-to-r from-primary/50 to-transparent" />
                  )}
                </div>
                <h3 className="text-xl font-semibold mb-2">{step.title}</h3>
                <p className="text-muted-foreground text-sm">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 lg:py-20 bg-gradient-to-b from-transparent via-secondary/30 to-transparent">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Why Choose <span className="bg-gradient-to-r from-primary to-purple-500 bg-clip-text text-transparent">TAM Store</span>?
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Built with love for developers and creators worldwide.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <div
                key={feature.title}
                className="animate-slide-up group"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="relative h-full p-6 rounded-2xl bg-card/50 border border-border/50 backdrop-blur-sm hover:border-primary/50 transition-all duration-300 hover:shadow-lg text-center overflow-hidden">
                  <div className={`absolute inset-0 bg-gradient-to-br ${feature.gradient} opacity-0 group-hover:opacity-5 transition-opacity`} />

                  <div className={`w-14 h-14 mx-auto rounded-2xl bg-gradient-to-br ${feature.gradient} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform shadow-lg`}>
                    <feature.icon className="h-7 w-7 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">{feature.title}</h3>
                  <p className="text-muted-foreground text-sm">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 lg:py-20">
        <div className="container mx-auto px-4">
          <div className="relative max-w-4xl mx-auto p-8 md:p-12 rounded-3xl bg-gradient-to-br from-primary/20 via-purple-500/10 to-pink-500/20 border border-primary/30 overflow-hidden">
            {/* Decorative elements */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-primary/20 rounded-full blur-3xl" />
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-purple-500/20 rounded-full blur-3xl" />

            <div className="relative text-center">
              <div className="flex items-center justify-center gap-2 mb-4">
                <Users className="h-6 w-6 text-primary" />
                <span className="text-primary font-medium">Join 10,000+ professionals</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Ready to Boost Your{' '}
                <span className="bg-gradient-to-r from-primary to-purple-500 bg-clip-text text-transparent">Productivity</span>?
              </h2>
              <p className="text-muted-foreground mb-8 max-w-xl mx-auto">
                Join thousands of developers and creators using TAM Store to streamline their workflow. It's free forever!
              </p>
              {!user && (
                <Link to="/signup">
                  <Button size="lg" className="gap-2 px-8 h-14 text-lg bg-gradient-to-r from-primary to-purple-600 hover:from-primary/90 hover:to-purple-600/90 shadow-lg shadow-primary/25 hover:shadow-primary/40 transition-all hover:scale-105">
                    Start Free Now
                    <ArrowRight className="h-5 w-5" />
                  </Button>
                </Link>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-border/50 bg-card/30 backdrop-blur-sm">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-8 items-center">
            {/* Logo & Name */}
            <div className="flex items-center gap-3 justify-center md:justify-start">
              <img src={tamLogo} alt="TAM Store" className="h-12 w-12 rounded-full border-2 border-primary/30" />
              <div>
                <span className="font-bold text-xl bg-gradient-to-r from-primary to-purple-500 bg-clip-text text-transparent">TAM Store</span>
                <p className="text-xs text-muted-foreground">Your Ultimate Digital Toolkit</p>
              </div>
            </div>

            {/* Links */}
            <div className="flex flex-wrap justify-center gap-6 text-sm text-muted-foreground">
              <span
                className="hover:text-primary cursor-pointer transition-colors"
                onClick={() => window.open('https://t.me/tam_tech786', '_blank')}
              >
                About
              </span>
              <span
                className="hover:text-primary cursor-pointer transition-colors"
                onClick={() => window.open('https://whatsapp.com/channel/0029VbAozL4FMqrRJcbpJc1K', '_blank')}
              >
                Contact
              </span>
              <span
                className="hover:text-primary cursor-pointer transition-colors"
                onClick={() => window.open('https://t.me/tam_tech786', '_blank')}
              >
                Privacy
              </span>
              <span
                className="hover:text-primary cursor-pointer transition-colors"
                onClick={() => window.open('https://t.me/tam_tech786', '_blank')}
              >
                Terms
              </span>
            </div>

            {/* Social Links */}
            <div className="flex items-center justify-center md:justify-end gap-3">
              <Button
                variant="outline"
                size="sm"
                className="gap-2 border-primary/30 hover:bg-primary/10 hover:border-primary"
                onClick={() => window.open('https://t.me/tam_tech786', '_blank')}
              >
                <Send className="h-4 w-4" />
                Telegram
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="gap-2 border-green-500/30 hover:bg-green-500/10 hover:border-green-500 text-green-400"
                onClick={() => window.open('https://whatsapp.com/channel/0029VbAozL4FMqrRJcbpJc1K', '_blank')}
              >
                <MessageCircle className="h-4 w-4" />
                WhatsApp
              </Button>
            </div>
          </div>

          <div className="mt-8 pt-8 border-t border-border/30 text-center">
            <p className="text-sm text-muted-foreground">
              © {new Date().getFullYear()} TAM Store. Powered by{' '}
              <span className="bg-gradient-to-r from-primary to-purple-500 bg-clip-text text-transparent font-medium">TAM Tech</span>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Index;
